/**
 * Shared data hook for loading every sub-catalog (custom catalog) of a
 * distributor, paging through `listSubCatalogs` until the connection is
 * exhausted. Used by the chat Settings sub-catalog select and the supplier
 * catalog tab's sub-catalog dropdown.
 */

import { useEffect, useState } from 'react';

import {
  ListSubCatalogsQuery,
  useListSubCatalogsLazyQuery,
} from '../generated';

export const SUB_CATALOG_PAGE_SIZE = 100;

export interface SubCatalog {
  id: string;
  name: string;
  supplierEntityId: string;
}

export interface UseListAllSubCatalogsResult {
  subCatalogs: SubCatalog[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

type LoadSubCatalogPage = (after?: string) => Promise<ListSubCatalogsQuery>;

export const loadAllSubCatalogs = async (
  loadPage: LoadSubCatalogPage
): Promise<SubCatalog[]> => {
  const subCatalogs: SubCatalog[] = [];
  const seenCursors = new Set<string>();
  const loadRemaining = async (after?: string): Promise<SubCatalog[]> => {
    const data = await loadPage(after);
    const connection = data.listSubCatalogs;

    if (!connection || connection.__typename !== 'SubCatalogConnection') {
      throw new Error('Failed to load custom catalogs');
    }

    if (connection.edges.length === 0) {
      return subCatalogs;
    }

    subCatalogs.push(
      ...connection.edges.map((edge) => ({
        id: edge.node.id,
        name: edge.node.name,
        supplierEntityId: edge.node.supplierEntityId,
      }))
    );

    const nextCursor = connection.edges.at(-1)?.cursor;
    if (!nextCursor || nextCursor === after || seenCursors.has(nextCursor)) {
      throw new Error('Catalog pagination did not advance');
    }

    seenCursors.add(nextCursor);
    return loadRemaining(nextCursor);
  };

  return loadRemaining();
};

interface SubCatalogLoadState {
  supplierId?: string;
  subCatalogs: SubCatalog[];
  isLoading: boolean;
  error: Error | null;
}

export const useListAllSubCatalogs = (
  supplierId?: string
): UseListAllSubCatalogsResult => {
  const [loadPage] = useListSubCatalogsLazyQuery({
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'none',
  });
  const [state, setState] = useState<SubCatalogLoadState>({
    supplierId,
    subCatalogs: [],
    isLoading: Boolean(supplierId),
    error: null,
  });
  const [refetchToken, setRefetchToken] = useState(0);

  useEffect(() => {
    let isActive = true;

    if (!supplierId) {
      setState({
        supplierId,
        subCatalogs: [],
        isLoading: false,
        error: null,
      });
      return () => {
        isActive = false;
      };
    }

    setState({
      supplierId,
      subCatalogs: [],
      isLoading: true,
      error: null,
    });

    loadAllSubCatalogs(async (after) => {
      const result = await loadPage({
        variables: {
          supplierEntityId: supplierId,
          first: SUB_CATALOG_PAGE_SIZE,
          after,
        },
      });

      if (result.error) {
        throw new Error(result.error.message);
      }

      if (!result.data) {
        throw new Error('Failed to load custom catalogs');
      }

      return result.data;
    })
      .then((subCatalogs) => {
        if (isActive) {
          setState({
            supplierId,
            subCatalogs,
            isLoading: false,
            error: null,
          });
        }
      })
      .catch((loadError: unknown) => {
        if (isActive) {
          setState({
            supplierId,
            subCatalogs: [],
            isLoading: false,
            error:
              loadError instanceof Error
                ? loadError
                : new Error('Failed to load custom catalogs'),
          });
        }
      });

    return () => {
      isActive = false;
    };
  }, [loadPage, supplierId, refetchToken]);

  const isCurrentSupplier = state.supplierId === supplierId;

  return {
    subCatalogs: isCurrentSupplier ? state.subCatalogs : [],
    isLoading: Boolean(supplierId) && (!isCurrentSupplier || state.isLoading),
    error: isCurrentSupplier ? state.error : null,
    refetch: () => setRefetchToken((token) => token + 1),
  };
};
